import React from 'react';
import { View, Pressable, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { Text } from '@/components/Text';
import { styles } from './styles';
import { Icon } from '@/components/Icon';
import colors from '@/config/colors';
import { useOnHover } from '@/hooks/useOnHover';
import { useOnFocus } from '@/hooks/useOnFocus';

interface CheckboxProps {
  isSelected: boolean;
  onPress: () => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  label?: string;
  labelStyle?: StyleProp<TextStyle>;
}

const Checkbox: React.FC<CheckboxProps> = ({
  isSelected,
  onPress,
  disabled = false,
  style,
  label,
  labelStyle,
}) => {
  const { isHovered, handleHoverIn, handleHoverOut } = useOnHover(disabled);
  const { isFocused, handleFocus, handleBlur } = useOnFocus(disabled);

  const getCheckboxStyle = () => {
    if (disabled) {
      return isSelected ? styles.disabledSelected : styles.disabled;
    }
    if (isFocused) {
      return isSelected ? styles.focusedSelected : styles.focused;
    }
    if (isHovered) {
      return isSelected ? styles.hoveredSelected : styles.hovered;
    }
    return isSelected ? styles.defaultSelected : styles.default;
  };

  const renderIcon = () => {
    if (isSelected) {
      return <Icon name="check" color="white" size={25} />;
    }
    return (
      <Icon
        name="plus"
        color={disabled ? 'transparent' : colors.newUI.therapist.blue60}
        size={25}
      />
    );
  };

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={[styles.checkboxContainer, style]}
    >
      <View style={styles.labelContainer}>
        <View style={[styles.focusWrapper, isFocused && styles.focusOutline]}>
          <View style={[styles.checkboxBase, getCheckboxStyle()]}>
            {renderIcon()}
          </View>
        </View>
        {!!label && <Text style={[styles.label, labelStyle]}>{label}</Text>}
      </View>
    </Pressable>
  );
};

export default Checkbox;
